import React from 'react';

import { SIGNAL_GROUPS } from './signalProviders';
import type { ISignalChip, SignalIcon, SignalTone } from './signalProviders';

const TONE_CLASS: Record<SignalTone, string> = {
  ok: "bg-green-50 text-green-700",
  warn: "bg-amber-50 text-amber-700",
  bad: "bg-red-50 text-red-700",
  unk: "bg-gray-100 text-gray-600",
};

const ICON_PATH: Record<SignalIcon, string> = {
  check: "M12 2.6a9.4 9.4 0 1 1 0 18.8 9.4 9.4 0 0 1 0-18.8Zm4.2 6-5.6 5.6-2.8-2.8-1.3 1.3 4.1 4.2 6.9-6.9-1.3-1.4Z",
  warn: "M12 2.2 23 21H1L12 2.2Zm0 3.6L4.1 19.1h15.8L12 5.8Zm-.9 4h1.8v5h-1.8v-5Zm0 6.2h1.8v1.8h-1.8v-1.8Z",
  violation:
    "M8.7 1h6.6L23 8.7v6.6L15.3 23H8.7L1 15.3V8.7L8.7 1Zm.8 2L3 9.5v5L9.5 21h5l6.5-6.5v-5L14.5 3h-5Zm1.6 3.4h1.8v6.4h-1.8V6.4Zm0 8h1.8v1.9h-1.8v-1.9Z",
  unknown:
    "M12 2.6a9.4 9.4 0 1 1 0 18.8 9.4 9.4 0 0 1 0-18.8Zm.1 3.6c-2 0-3.3 1.1-3.6 3l1.8.3c.2-1.1.8-1.7 1.8-1.7.9 0 1.5.5 1.5 1.3 0 .6-.3 1-1.2 1.7-1 .8-1.4 1.5-1.4 2.6v.6h1.8v-.4c0-.8.2-1.1 1.1-1.8 1.1-.9 1.6-1.6 1.6-2.8 0-1.7-1.4-2.8-3.4-2.8Zm-1.1 9.6v1.9h1.9v-1.9H11Z",
  dot: "M12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10Z",
  clock: "M12 2.6a9.4 9.4 0 1 1 0 18.8 9.4 9.4 0 0 1 0-18.8Zm-.9 3.8v6.3l4.7 2.8.9-1.5-3.8-2.3V6.4h-1.8Z",
};

interface IProps {
  chips: ISignalChip[];
  /** Called when a chip that carries an action is clicked. */
  onChipAction?: (action: NonNullable<ISignalChip["action"]>) => void;
}

const CHIP_CLASS = "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-bold";

/** Every group always renders its heading, so the card layout is the same whether or not data has arrived. */
export const SignalChips = ({ chips, onChipAction }: IProps) => (
  <div
    className="mt-2 border-t border-gray-200 pt-1"
    data-testid="signal-chips"
  >
    {SIGNAL_GROUPS.map((group) => (
      <div key={group} className="py-1.5" data-testid={`signal-group-${group}`}>
        <div className="mb-1 text-[11px] font-bold text-gray-400">{group}</div>
        <div className="flex flex-wrap gap-1.5">
          {chips
            .filter((chip) => chip.group === group)
            .map((chip) => {
              const content = (
                <>
                  <svg
                    viewBox="0 0 24 24"
                    className="h-3.5 w-3.5 flex-none"
                    aria-hidden="true"
                  >
                    <path d={ICON_PATH[chip.icon]} fill="currentColor" />
                  </svg>
                  {chip.text}
                </>
              );
              const action = chip.action;
              return action && onChipAction ? (
                <button
                  key={chip.key}
                  type="button"
                  title={chip.title}
                  className={`${CHIP_CLASS} ${TONE_CLASS[chip.tone]} cursor-pointer hover:brightness-95`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChipAction(action);
                  }}
                >
                  {content}
                </button>
              ) : (
                <span
                  key={chip.key}
                  title={chip.title}
                  className={`${CHIP_CLASS} ${TONE_CLASS[chip.tone]}`}
                >
                  {content}
                </span>
              );
            })}
        </div>
      </div>
    ))}
  </div>
);
